import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@chakra-ui/icons';
import {
    Flex,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    StyleObjectOrFn,
    Text,
    css,
    useTheme,
} from '@chakra-ui/react';
import { ClassNames } from '@emotion/react';
import { nl } from 'date-fns/locale';
import { FC, forwardRef, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isWeekday } from '../helpers/date';

const InputPrimitive: FC = forwardRef<any>((props, ref) => {
    return (
        <InputGroup>
            <Input {...props} ref={ref} autoComplete="new-password" />
            <InputRightElement pointerEvents={'none'} userSelect={'none'}>
                <CalendarIcon />
            </InputRightElement>
        </InputGroup>
    );
});

type HeaderProps = {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
};

const HeaderPrimitive: FC<HeaderProps> = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}) => {
    return (
        <HStack pb={1} alignItems="center" textAlign="left" pl={4} pr={2}>
            <Text
                fontWeight={'bold'}
                flex={1}
                fontSize={'md'}
                textTransform={'capitalize'}
            >
                {new Intl.DateTimeFormat('nl-NL', {
                    year: 'numeric',
                    month: 'long',
                }).format(date)}
            </Text>

            <IconButton
                icon={<ChevronLeftIcon />}
                aria-label={'Vorige maand'}
                disabled={prevMonthButtonDisabled}
                onClick={decreaseMonth}
                variant={'ghost'}
                size={'md'}
            />

            <IconButton
                icon={<ChevronRightIcon />}
                aria-label={'Volgende maand'}
                disabled={nextMonthButtonDisabled}
                onClick={increaseMonth}
                variant={'ghost'}
                size={'md'}
            />
        </HStack>
    );
};

const datePickerStyles = () => {
    const theme = useTheme();

    return useMemo(() => {
        const defaultStyles: StyleObjectOrFn = {
            p: 2,
            bgColor: 'white',
            borderWidth: 1,
            borderColor: 'gray.200',
            boxShadow: 'md',
            '& .react-datepicker': {
                '&__header': {
                    bg: 'none',
                    borderBottom: 'none',
                },
                '&__month': {
                    mt: 0,
                },
                '&__day-name': {
                    color: 'gray.500',
                    fontWeight: 'bold',
                    w: 7,
                },
                '&__day': {
                    lineHeight: '28px',
                    color: 'gray.700',
                    w: 7,
                    h: 7,
                    borderRadius: 'full',
                },
                '&__day:not(.react-datepicker__day--selected, .react-datepicker__day--disabled, .react-datepicker__day--keyboard-selected):hover':
                    {
                        bgColor: 'gray.200',
                    },
                '&__day--today': {
                    bg: 'gray.100',
                    fontWeight: '400',
                },
                '&__day--selected, &__day--keyboard-selected': {
                    bgColor: 'secondary',
                    color: 'white',
                },
            },
            '& .react-datepicker__day--disabled': {
                color: 'gray.400',
            },
        };

        return css(defaultStyles)(theme);
    }, [theme]);
};

type Props = {
    value: Date | null;
    onChange: (value: Date | null) => void;
    disabled?: boolean;
    minDate?: Date;
};

export const DateInput: FC<Props> = ({
    value,
    onChange,
    disabled,
    minDate = new Date(),
}) => {
    const styles = datePickerStyles();

    const render = useCallback(
        ({ css }: { css: any }) => {
            return (
                <DatePicker
                    autoComplete="new-password" // disable autocomplete
                    dateFormat={'dd-MM-yyyy'}
                    customInput={<InputPrimitive />}
                    renderCustomHeader={HeaderPrimitive}
                    showPopperArrow={false}
                    selected={value}
                    onChange={onChange}
                    calendarClassName={css(styles)}
                    filterDate={isWeekday}
                    calendarStartDay={1}
                    minDate={minDate}
                    locale={nl}
                    disabled={disabled}
                    wrapperClassName={css({ flex: 1 })}
                />
            );
        },
        [styles, value]
    );

    return (
        <Flex justifyContent={'stretch'}>
            <ClassNames>{render}</ClassNames>
        </Flex>
    );
};
